// src/components/AdminDashboard.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { db } from '../firebase';
import { ref, onValue } from 'firebase/database';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export default function Dashboard() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewAll, setViewAll] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    const studentsRef = ref(db, 'students');
    const unsubscribe = onValue(studentsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const studentsArray = Object.entries(data).map(([id, value]) => ({
          id,
          ...value,
        }));
        setStudents(studentsArray);
      } else {
        setStudents([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const toggleView = () => {
    setViewAll(!viewAll);
    setSelectedStudent(null);
  };

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setViewAll(false);
  };

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const aggregateTotals = (studentList) => {
    return studentList.reduce(
      (acc, student) => {
        if (student.dailyMetrics) {
          Object.values(student.dailyMetrics).forEach((m) => {
            acc.applicationsSubmitted += m.applicationsSubmitted || 0;
            acc.connectionsAdded += m.connectionsAdded || 0;
            acc.hoursStudied += m.hoursStudied || 0;
            acc.phoneCalls += m.phoneCalls || 0;
            acc.techInterviews += m.techInterviews || 0;
          });
        }
        return acc;
      },
      {
        applicationsSubmitted: 0,
        connectionsAdded: 0,
        hoursStudied: 0,
        phoneCalls: 0,
        techInterviews: 0,
      },
    );
  };

  const filteredStudents = useMemo(() => {
    return students
      .filter((s) =>
        [s.name, s.lastName, s.jobTitle, s.code]
          .join(' ')
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
      .sort((a, b) => {
        if (sortBy === 'applications') {
          const totalA = Object.values(a.dailyMetrics || {}).reduce(
            (acc, m) => acc + (m.applicationsSubmitted || 0),
            0,
          );
          const totalB = Object.values(b.dailyMetrics || {}).reduce(
            (acc, m) => acc + (m.applicationsSubmitted || 0),
            0,
          );
          return totalB - totalA;
        }
        if (sortBy === 'hours') {
          const totalA = Object.values(a.dailyMetrics || {}).reduce(
            (acc, m) => acc + (m.hoursStudied || 0),
            0,
          );
          const totalB = Object.values(b.dailyMetrics || {}).reduce(
            (acc, m) => acc + (m.hoursStudied || 0),
            0,
          );
          return totalB - totalA;
        }
        return a.name.localeCompare(b.name);
      });
  }, [students, search, sortBy]);

  // Top 3 students leaderboard
  const topPerformers = useMemo(() => {
    return [...filteredStudents]
      .map((s) => ({
        ...s,
        totalApplications: Object.values(s.dailyMetrics || {}).reduce(
          (acc, m) => acc + (m.applicationsSubmitted || 0),
          0,
        ),
        totalHours: Object.values(s.dailyMetrics || {}).reduce(
          (acc, m) => acc + (m.hoursStudied || 0),
          0,
        ),
      }))
      .sort((a, b) => b.totalApplications - a.totalApplications)
      .slice(0, 3);
  }, [filteredStudents]);

  const renderLeaderboard = () => {
    if (topPerformers.length === 0) return null;
    return (
      <div className='mb-8'>
        <h2 className='text-2xl font-bold text-white mb-4'>Top Performers</h2>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          {topPerformers.map((s, idx) => (
            <div
              key={s.id}
              className='bg-yellow-400 p-4 rounded shadow text-center text-white cursor-pointer'
            >
              <p className='text-lg font-semibold'>
                {idx + 1}. {s.name} {s.lastName}
              </p>
              <p>Total Applications: {s.totalApplications}</p>
              <p>Total Hours: {s.totalHours}</p>
              <button
                onClick={() => handleSelectStudent(s)}
                className='mt-2 px-3 py-1 bg-white text-yellow-500 rounded hover:bg-gray-200'
              >
                View Analytics
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAllStudents = () => {
    const totals = aggregateTotals(filteredStudents);

    return (
      <>
        <div className='mb-6 flex flex-col md:flex-row justify-between items-center gap-4'>
          <h1 className='text-4xl font-bold text-white'>Admin Dashboard</h1>
          <div className='flex flex-wrap gap-2 items-center'>
            <input
              type='text'
              placeholder='Search students...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className='px-3 py-2 rounded border focus:outline-none'
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className='px-3 py-2 rounded border focus:outline-none'
            >
              <option value='name'>Sort by Name</option>
              <option value='applications'>Sort by Applications</option>
              <option value='hours'>Sort by Hours</option>
            </select>
            <button
              onClick={toggleView}
              className='bg-blue-600 px-4 py-2 rounded text-white hover:bg-blue-700 transition'
            >
              View Single Student
            </button>
          </div>
        </div>

        {/* Leaderboard */}
        {renderLeaderboard()}

        {/* Summary cards */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
          <SummaryCard
            title='Total Students'
            value={filteredStudents.length}
            color='from-pink-400 to-red-500'
          />
          <SummaryCard
            title='Total Applications'
            value={totals.applicationsSubmitted}
            color='from-green-400 to-teal-500'
          />
          <SummaryCard
            title='Total Hours Studied'
            value={totals.hoursStudied}
            color='from-indigo-500 to-blue-700'
          />
        </div>

        {/* Collapsible student cards */}
        <div className='space-y-4'>
          {filteredStudents.map((student) => (
            <StudentCard
              key={student.id}
              student={student}
              onView={() => handleSelectStudent(student)}
              formatDate={formatDate}
            />
          ))}
        </div>
      </>
    );
  };

  const renderSingleStudent = () => {
    const student = selectedStudent || filteredStudents[0];
    if (!student)
      return <p className='text-white text-center mt-10'>No student found</p>;

    const data = student.dailyMetrics
      ? Object.entries(student.dailyMetrics).map(([date, m]) => ({
          date: formatDate(date),
          Applications: m.applicationsSubmitted || 0,
          Connections: m.connectionsAdded || 0,
          PhoneCalls: m.phoneCalls || 0,
          TechInterviews: m.techInterviews || 0,
          Hours: m.hoursStudied || 0,
        }))
      : [];

    return (
      <>
        <div className='mb-6 flex justify-between items-center'>
          <h1 className='text-3xl font-bold text-white'>
            {student.name} {student.lastName}
          </h1>
          <button
            onClick={toggleView}
            className='bg-blue-600 px-4 py-2 rounded text-white hover:bg-blue-700 transition'
          >
            View All Students
          </button>
        </div>

        {data.length > 0 ? (
          <ResponsiveContainer width='100%' height={300}>
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
            >
              <XAxis dataKey='date' />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey='Applications' fill='#f56565' />
              <Bar dataKey='Connections' fill='#48bb78' />
              <Bar dataKey='PhoneCalls' fill='#4299e1' />
              <Bar dataKey='TechInterviews' fill='#9f7aea' />
              <Bar dataKey='Hours' fill='#ecc94b' />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className='text-white mt-4'>No metrics recorded yet.</p>
        )}

        <div className='mt-6 space-y-2'>
          {student.dailyMetrics &&
            Object.entries(student.dailyMetrics)
              .sort(([a], [b]) => new Date(b) - new Date(a))
              .map(([date, m]) => (
                <div key={date} className='bg-white p-4 rounded shadow'>
                  <p className='font-semibold'>{formatDate(date)}</p>
                  <p>Applications Submitted: {m.applicationsSubmitted}</p>
                  <p>Connections Added: {m.connectionsAdded}</p>
                  <p>Phone Calls: {m.phoneCalls}</p>
                  <p>Tech Interviews: {m.techInterviews}</p>
                  <p>Hours Studied: {m.hoursStudied}</p>
                  {m.selfReflection && <p>Reflection: {m.selfReflection}</p>}
                </div>
              ))}
        </div>
      </>
    );
  };

  if (loading)
    return <p className='text-white text-center mt-20'>Loading...</p>;

  return (
    <div className='min-h-screen bg-gradient-to-br from-purple-700 via-indigo-800 to-blue-900 p-8'>
      <div className='max-w-7xl mx-auto'>
        {viewAll ? renderAllStudents() : renderSingleStudent()}
      </div>
    </div>
  );
}

// StudentCard and SummaryCard remain unchanged
function StudentCard({ student, onView, formatDate }) {
  const [open, setOpen] = useState(false);
  const daily = student.dailyMetrics
    ? Object.entries(student.dailyMetrics).sort(
        ([a], [b]) => new Date(b) - new Date(a),
      )
    : [];

  return (
    <div className='bg-white rounded shadow p-4'>
      <div
        className='flex justify-between items-center cursor-pointer'
        onClick={() => setOpen(!open)}
      >
        <div>
          <p className='font-semibold text-lg'>
            {student.name} {student.lastName}
          </p>
          <p className='text-sm text-gray-600'>{student.jobTitle || '-'}</p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          className='bg-blue-600 px-3 py-1 text-white rounded hover:bg-blue-700'
        >
          View Analytics
        </button>
      </div>
      {open && (
        <div className='mt-2 space-y-1 border-t pt-2'>
          {daily.length === 0 ? (
            <p className='text-gray-500'>No metrics recorded</p>
          ) : (
            daily.map(([date, m]) => (
              <div key={date} className='p-2 border rounded bg-gray-50'>
                <p className='font-semibold'>{formatDate(date)}</p>
                <p>Applications: {m.applicationsSubmitted}</p>
                <p>Connections: {m.connectionsAdded}</p>
                <p>Phone Calls: {m.phoneCalls}</p>
                <p>Tech Interviews: {m.techInterviews}</p>
                <p>Hours Studied: {m.hoursStudied}</p>
                {m.selfReflection && <p>Reflection: {m.selfReflection}</p>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ title, value, color }) {
  return (
    <div
      className={`p-6 bg-gradient-to-tr ${color} rounded-xl shadow-lg text-center text-white cursor-pointer`}
    >
      <h2 className='text-xl font-semibold mb-2'>{title}</h2>
      <p className='text-4xl font-bold'>{value}</p>
    </div>
  );
}
